import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useIntl, getLocale } from 'umi';
import { useMemoizedFn } from 'ahooks';
import {
  Button,
  Form,
  Modal,
  ModalProps,
  SelectMultiple,
  Bubble,
  notification,
} from 'tea-component';
import useFeedbackGenreSelect from './_hooks/useFeedbackGenreSelect';
import { updateGameGenres } from '@intelligence/services/topcharts';
import { buildSelectFilterAccepter } from '@intelligence/utils/commonUtils';
import { analysisMainGenresAndSubGenres, generateGenreStr, renderButton } from './utils';
// import { OPTIONS_NAMESPACE } from '@intelligence/models/options';
import useGenreSelector from '@/pages/intelligence/hooks/useGenreSelector';
import type { IGameType, IGenreMetadata } from '@intelligence/types/common';
// import type { IOptionsState } from '@intelligence/models/options';

import './Modal.scss';

// 主品类数量上限
const MAIN_GENRES_LIMIT = 1;
// 副品类数量上限
const SUB_GENRES_LIMIT = 2;
// 模糊搜索时，是否忽略大小写
const IGNORECASE = true;
// 模糊搜索时，是否拆分为单个字符
const SEPARATE = true;

export interface IGenreFeedbackData {
  entityType: IGameType;
  entityId: string;
  entityName: string;
  isIegg: boolean;
  mainGenre: string;
  subGenre: string;
}

interface IGenreFeedbackModalProps extends ModalProps, IGenreFeedbackData {}

const GenreFeedbackModal: React.FC<IGenreFeedbackModalProps> = (porps) => {
  const {
    entityType,
    entityId,
    entityName,
    isIegg,
    mainGenre,
    subGenre,
    visible,
    onClose,
    style,
    className,
    ...restProps
  } = porps;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  // const { genres: genresState } = useSelector<any, IOptionsState>(
  //   (state) => state?.[OPTIONS_NAMESPACE],
  // );

  // 使用统一全局存储数据
  const { genreOptions, filterLoading } = useGenreSelector({});

  // const dispatch = useDispatch();
  // useEffect(() => {
  //   // 获取品类全量options数据
  //   dispatch({
  //     type: `${OPTIONS_NAMESPACE}/getGenresOptions`,
  //     payload: { entityType },
  //   });
  // }, []);
  // 主品类options、副品类options
  const { mainGenres, subGenres } = useMemo<{
    mainGenres: IGenreMetadata[];
    subGenres: IGenreMetadata[];
  }>(() => {
    if (filterLoading)
      return {
        mainGenres: [],
        subGenres: [],
      };
    return analysisMainGenresAndSubGenres(isIegg, genreOptions);
  }, [isIegg, filterLoading, genreOptions]);

  // 主品类
  const {
    keywords: mainKeywords,
    options: mainOptions,
    value: mainValue,
    onChange: onMainChange,
    onDelete: onMainDelete,
    onSearch: onMainSearch,
    onOpen: onMainOpen,
  } = useFeedbackGenreSelect({
    genres: mainGenres,
    genre: mainGenre,
    resetFlag: !visible,
    ignoreCase: IGNORECASE,
    separate: SEPARATE,
  });

  // 副品类
  const {
    keywords: subKeywords,
    options: subOptions,
    value: subValue,
    onChange: onSubChange,
    onDelete: onSubDelete,
    onSearch: onSubSearch,
    onOpen: onSubOpen,
  } = useFeedbackGenreSelect({
    genres: subGenres,
    genre: subGenre,
    resetFlag: !visible,
    ignoreCase: IGNORECASE,
    separate: SEPARATE,
  });

  const [submitting, setSubmitting] = useState(false);

  // 品类调整限制条件：一主两副（一个主品类，两个副品类），主品类、副品类都至少选择一个
  const disabled = useMemo<boolean>(() => {
    if (!Array.isArray(mainValue) || !Array.isArray(subValue)) return true;
    const isMainValueValid = mainValue?.length >= 1 && mainValue?.length <= MAIN_GENRES_LIMIT;
    const isSubValueValid = subValue?.length >= 1 && subValue?.length <= SUB_GENRES_LIMIT;
    return !(isMainValueValid && isSubValueValid);
  }, [mainValue, subValue]);

  const confirmButtonTips = useMemo<string | undefined>(() => {
    if (!disabled) return undefined;
    return formatMessage({ id: 'dbi_genrefeedback_disabled_confirm_tips' });
  }, [locale, disabled]);

  // SelectMultiple组件自定义搜索逻辑
  const filterAccepter = useMemo(() => {
    return buildSelectFilterAccepter({
      ignoreCase: IGNORECASE,
      separate: SEPARATE,
      getOptionText: (option) => option?.label || '',
    });
  }, []);

  const handleConfirm = useMemoizedFn(async () => {
    try {
      setSubmitting(true);
      await updateGameGenres({
        entity_type: entityType,
        game_id: entityId,
        entity_name: entityName,
        genre: mainGenre,
        sub_genre: subGenre,
        genre_new: generateGenreStr(mainValue),
        sub_genre_new: generateGenreStr(subValue),
      });
      notification.success({
        // description: formatMessage({ id: 'dbi_operation_successfully_tips' }),
        description: formatMessage({ id: 'dbi_common_operation_succeed_tips' }),
      });
      onClose?.();
    } catch (exception) {
      notification.error({
        // description: formatMessage({ id: 'dbi_operation_failly_tips' }),
        description: formatMessage({ id: 'dbi_common_operation_failed_tips' }),
      });
    } finally {
      setSubmitting(false);
    }
  });

  const handleCancel = useMemoizedFn(() => {
    onClose?.();
  });

  const renderMainButton = useMemo(() => {
    return renderButton(onMainDelete, formatMessage);
  }, [locale, onMainDelete, formatMessage]);

  const renderSubButton = useMemo(() => {
    return renderButton(onSubDelete, formatMessage);
  }, [locale, onSubDelete, formatMessage]);

  return (
    <Modal
      {...restProps}
      style={style}
      className={classnames('dbi-genre-feedback-modal', className)}
      caption={formatMessage({ id: 'dbi_genrefeedback_title' })}
      size={600}
      visible={visible}
      onClose={onClose}
    >
      <Modal.Body>
        <div className="dbi-genre-feedback-text">
          {formatMessage({ id: 'dbi_genrefeedback_desc' })}
        </div>
        <Form className="dbi-genre-feedback-form">
          {/* 主品类 */}
          <Form.Item label={formatMessage({ id: 'dbi_main_genre' })}>
            <SelectMultiple
              searchable
              matchButtonWidth
              className="dbi-genre-feedback-select"
              overlayClassName="dbi-genre-feedback-overlay"
              searchPlaceholder={formatMessage({
                id: 'dbi_genrefeedback_main_genre_search_placeholder',
              })}
              appearance="button"
              staging={false}
              searchValue={mainKeywords}
              // @ts-ignore
              filter={filterAccepter}
              // @ts-ignore
              button={renderMainButton}
              options={mainOptions}
              value={mainValue}
              onChange={onMainChange}
              onSearch={onMainSearch}
              onOpen={onMainOpen}
            />
          </Form.Item>
          {/* 副品类 */}
          <Form.Item label={formatMessage({ id: 'dbi_sub_genre' })}>
            <SelectMultiple
              searchable
              matchButtonWidth
              className="dbi-genre-feedback-select"
              overlayClassName="dbi-genre-feedback-overlay"
              searchPlaceholder={formatMessage({
                id: 'dbi_genrefeedback_sub_genre_search_placeholder',
              })}
              appearance="button"
              staging={false}
              searchValue={subKeywords}
              // @ts-ignore
              filter={filterAccepter}
              // @ts-ignore
              button={renderSubButton}
              options={subOptions}
              value={subValue}
              onChange={onSubChange}
              onSearch={onSubSearch}
              onOpen={onSubOpen}
            />
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Bubble dark arrowPointAtCenter content={confirmButtonTips}>
          <Button type="primary" loading={submitting} disabled={disabled} onClick={handleConfirm}>
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
        </Bubble>
        <Button type="weak" onClick={handleCancel}>
          {formatMessage({ id: 'dbi_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(GenreFeedbackModal);
